import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import { formatMoney } from "../money.ts";
import type {
  HistoryWindow,
  OwnCommerceOrder,
  OwnDeliveryOrder,
  OwnTrip,
  RiderApi,
} from "../rider-api.ts";
import { SCOPES } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import {
  EmptyRow,
  Group,
  InfoNote,
  MarketShell,
  type BadgeValue,
  type RowValue,
} from "./MarketShell.tsx";
import { MarketState } from "./TrancheState.tsx";

/**
 * `Order history` — a rider's rides, orders and parcels in one list, from three listings.
 *
 * | Drawn                                  | From                                                      |
 * | -------------------------------------- | --------------------------------------------------------- |
 * | `Westlands → JKIA`                     | TRP-01's `pickupLabel` and `dropoffLabel`                  |
 * | `KES 2,290`                            | TRP-01's `fare.amountMinor` and `fare.currency`            |
 * | `completed` / `cancelled`              | TRP-01's `status`                                          |
 * | `KES 529` on a shop order              | COM-05's `totalAmountMinor` and `currency`                 |
 * | `Kilimani Fresh` — the shop's name     | **nothing** — COM-05 publishes `merchantId`, MER-01 the name |
 * | `Parcel to Oyster Bay`                 | **nothing** — DEL-03 publishes `deliveryAddressId`         |
 * | `TZS 8,000` on a parcel                | **nothing** — DEL-03 publishes no money at all             |
 * | `retry scheduled`                      | **nothing** — DEL-03 publishes `failed`, not a retry plan  |
 * | `cancelled · free`                     | the word, not the fee — no cancellation fee is published   |
 * | `This week` / `Earlier`                | each record's own timestamp, grouped here                  |
 * | `September 2026`                       | the window actually read, not a fixed month                |
 * | `Rides, orders and parcels`            | designed copy                                              |
 * | `Tap any item for its receipt.`        | designed copy, which this build cannot honour — see below  |
 *
 * ## Why this screen could not be built until now, and what changed
 *
 * Its one blocking element was `Westlands → JKIA · KES 2,290` — where a trip went and what it
 * cost — and `navigation.ts` carried the measurement that blocked it: MOB-03's request and
 * MOB-02's quote are both public and both by identifier, so a fan-out worked and cost **two calls
 * per row**, four hundred for a full page. Worse than the cost, neither read takes a version while
 * TRP-01 pins `quoteVersion` deliberately, so a fan-out would have shown each quote **as it is
 * now** rather than what the rider was actually charged — a history that disagrees with the
 * receipt it is a history of.
 *
 * The decision taken was to record the fare and the two labels on the trip itself, and TRP-01's
 * migration did. So this screen reads one listing per domain and shows the figure that was
 * charged, which is the only figure a history may show.
 *
 * ## Three calls, reported separately
 *
 * There is no aggregate over a customer's whole activity, and ADR 0097 declined to build one: a
 * BFF exists for the read a channel cannot make, and three parallel reads are a read a phone makes
 * perfectly well. The consequence is handled rather than ignored — each source is reported on its
 * own, so DEL-03 being down draws a note naming parcels instead of a history that is quietly short
 * a few rows. A history missing rows without saying so is the one failure this screen must not
 * have, because it is indistinguishable from a rider who never sent a parcel.
 *
 * ## `Tap any item for its receipt.` is drawn and cannot be honoured
 *
 * The designed sub-line promises a receipt per row. No receipt screen is designed in this
 * deliverable and no operation returns one for a commerce order or a parcel, so the rows carry no
 * control and pressing one does nothing. The copy is kept word for word because `CLAUDE.md`
 * section 1 says copy is kept and that a design which seems wrong is asked about rather than
 * fixed, and the gap is flagged on screen rather than papered over.
 */

export interface OrderHistoryProps {
  readonly api: RiderApi;
  readonly scopes: ReadonlySet<string>;
  readonly onBack: () => void;
  /** Fixed by the caller in tests, so the window and the grouping are not read off the wall clock. */
  readonly now?: Date;
}

/** TRP-01's eight statuses against the chip the design draws for a trip. */
const TRIP_BADGE: Readonly<Record<OwnTrip["status"], BadgeValue>> = {
  assigned: { kind: "active", label: "assigned" },
  driver_en_route: { kind: "active", label: "driver on the way" },
  driver_arrived: { kind: "active", label: "driver arrived" },
  waiting: { kind: "active", label: "waiting" },
  in_trip: { kind: "active", label: "in trip" },
  completed: { kind: "done", label: "completed" },
  /*
   * The design writes `cancelled · free`, and the second half is a statement about a cancellation
   * fee. Nothing publishes one — TRP-01 records that a trip was cancelled, not what cancelling it
   * cost — so the word is drawn and the fee is not invented. Writing `· free` would be this build
   * telling a rider they were not charged, which it has no way to know.
   */
  cancelled: { kind: "closed", label: "cancelled" },
  no_show: { kind: "closed", label: "no show" },
};

/**
 * COM-05's three statuses.
 *
 * The design draws `delivered` on the shop-order row, which is not a word COM-05 publishes: its
 * vocabulary is `placed`, `completed` and `cancelled`. COM-05's own word is drawn rather than the
 * design's, because a status is the service's statement about its own record and translating it
 * here would put a confident voice on a guess. Recorded as a question.
 */
const COMMERCE_BADGE: Readonly<Record<OwnCommerceOrder["status"], BadgeValue>> = {
  placed: { kind: "active", label: "placed" },
  completed: { kind: "done", label: "completed" },
  cancelled: { kind: "closed", label: "cancelled" },
};

/** DEL-03's seven statuses. `retry scheduled` is not among them; see the table above. */
const DELIVERY_BADGE: Readonly<Record<OwnDeliveryOrder["status"], BadgeValue>> = {
  accepted: { kind: "active", label: "accepted" },
  pickup_ready: { kind: "active", label: "ready for pickup" },
  in_custody: { kind: "active", label: "in custody" },
  delivery_attempted: { kind: "caution", label: "delivery attempted" },
  delivered: { kind: "done", label: "delivered" },
  cancelled: { kind: "closed", label: "cancelled" },
  failed: { kind: "caution", label: "failed" },
};

const DAY_IN_MILLISECONDS = 86_400_000;

/**
 * The window a history is read over: the calendar month `now` falls in.
 *
 * A month because the chip names one, and the chip has to name the window it is actually showing
 * or it is decoration. `to` is the first instant of the next month and all three listings treat
 * the end as exclusive, so a trip at 23:59 on the last day is inside the window and nothing is
 * counted in two months at once.
 */
export function monthWindow(now: Date): HistoryWindow {
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
  const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  return { from: from.toISOString(), to: to.toISOString() };
}

/** `September 2026` — the window's own month, so the chip cannot disagree with the rows. */
export function monthLabel(now: Date): string {
  return new Intl.DateTimeFormat("en-GB", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(now);
}

function startOfDay(moment: Date): number {
  return Date.UTC(moment.getUTCFullYear(), moment.getUTCMonth(), moment.getUTCDate());
}

/**
 * `Yesterday 05:30`, `Mon`, `22 Sep` — the design's three shapes for when something happened.
 *
 * Which one is used depends on how long ago it was, which is what the design's own sample shows:
 * the most recent rows carry a time, ones earlier in the week carry a weekday, and anything older
 * carries a date. Computed against `now` rather than the wall clock, so a test fixes it.
 */
export function whenLabel(at: string, now: Date): string {
  const moment = new Date(at);
  if (Number.isNaN(moment.getTime())) return at;
  const days = Math.floor((startOfDay(now) - startOfDay(moment)) / DAY_IN_MILLISECONDS);
  const time = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "UTC",
  }).format(moment);
  if (days <= 0) return `Today ${time}`;
  if (days === 1) return `Yesterday ${time}`;
  if (days < 7) {
    return new Intl.DateTimeFormat("en-GB", { weekday: "short", timeZone: "UTC" }).format(moment);
  }
  /*
   * `22 Sep`, and the month is cut to three letters deliberately.
   *
   * `Intl` with `month: "short"` renders September as **`Sept`** under current ICU — four letters,
   * and the only month it does that for. The design writes `22 Sep`, so a formatter left to
   * itself draws one month differently from the other eleven and differently from the design.
   * Cutting to three is safe for all twelve: every other short month is already three letters.
   */
  const day = new Intl.DateTimeFormat("en-GB", { day: "numeric", timeZone: "UTC" }).format(moment);
  const month = new Intl.DateTimeFormat("en-GB", { month: "short", timeZone: "UTC" }).format(
    moment,
  );
  return `${day} ${month.slice(0, 3)}`;
}

/** `This week` and `Earlier`, the design's two groups, split at seven days. */
export function isThisWeek(at: string, now: Date): boolean {
  const moment = new Date(at);
  if (Number.isNaN(moment.getTime())) return false;
  return (startOfDay(now) - startOfDay(moment)) / DAY_IN_MILLISECONDS < 7;
}

/**
 * A trip's fare, or nothing.
 *
 * `amountMinor` arrives as a string because TRP-01 publishes it as one, and `formatMoney` takes a
 * number and throws on anything that is not whole minor units. A fare that does not parse is
 * treated as absent rather than drawn as `NaN` or allowed to throw and take the screen down with
 * it: one unpriceable trip must not empty a history.
 */
export function fareOf(trip: OwnTrip): string | undefined {
  if (trip.fare === undefined) return undefined;
  const minor = Number(trip.fare.amountMinor);
  if (!Number.isInteger(minor)) return undefined;
  return formatMoney(minor, trip.fare.currency);
}

/** One row per record, newest first, in the shape the design's `groups` draw. */
export function historyRows(
  trips: readonly OwnTrip[],
  orders: readonly OwnCommerceOrder[],
  parcels: readonly OwnDeliveryOrder[],
  now: Date,
): readonly { readonly at: string; readonly row: RowValue }[] {
  const rows: { at: string; row: RowValue }[] = [];

  for (const trip of trips) {
    const fare = fareOf(trip);
    const route =
      trip.pickupLabel !== undefined && trip.dropoffLabel !== undefined
        ? `${trip.pickupLabel} → ${trip.dropoffLabel}`
        : undefined;
    rows.push({
      at: trip.createdAt,
      row: {
        id: `trip-${trip.tripId}`,
        lead: "local_taxi",
        title: route ?? "",
        ...(route === undefined
          ? {
              titleAbsent:
                "No route: this trip was recorded before TRP-01 carried pickupLabel and dropoffLabel",
            }
          : {}),
        sub: whenLabel(trip.createdAt, now),
        badge: TRIP_BADGE[trip.status],
        ...(fare === undefined
          ? {
              amountAbsent:
                "No fare: TRP-01 carries one only for trips recorded since it began storing the charge",
            }
          : { amount: fare }),
      },
    });
  }

  for (const order of orders) {
    rows.push({
      at: order.placedAt,
      row: {
        id: `order-${order.orderId}`,
        lead: "shopping_basket",
        title: "",
        titleAbsent: "No shop name: COM-05 publishes merchantId, and MER-01 holds the name",
        sub: whenLabel(order.placedAt, now),
        badge: COMMERCE_BADGE[order.status],
        amount: formatMoney(order.totalAmountMinor, order.currency),
      },
    });
  }

  for (const parcel of parcels) {
    rows.push({
      at: parcel.createdAt,
      row: {
        id: `parcel-${parcel.orderId}`,
        lead: "package_2",
        title: "",
        titleAbsent: "No destination: DEL-03 publishes deliveryAddressId, not an address label",
        sub: whenLabel(parcel.createdAt, now),
        badge: DELIVERY_BADGE[parcel.status],
        amountAbsent: "No amount: DEL-03 publishes no total and no currency on a delivery order",
      },
    });
  }

  // Newest first, which is the order all three listings answer in and the order the design draws.
  return rows.sort((one, two) => two.at.localeCompare(one.at));
}

/** The shortest true thing that can be said about why one listing did not answer. */
function reasonFor(error: unknown): string {
  if (error !== null && typeof error === "object" && "status" in error) {
    const status = (error as { readonly status?: unknown }).status;
    if (status === 403) return "this sign-in is not granted that listing";
    if (typeof status === "number") return `the service answered ${String(status)}`;
  }
  return "the service could not be reached";
}

export function OrderHistory(props: OrderHistoryProps): ReactNode {
  const { api } = props;
  const fixedNow = props.now;
  const now = useMemo(() => fixedNow ?? new Date(), [fixedNow]);
  const readWindow = useMemo(() => monthWindow(now), [now]);

  const [trips, setTrips] = useState<readonly OwnTrip[]>([]);
  const [orders, setOrders] = useState<readonly OwnCommerceOrder[]>([]);
  const [parcels, setParcels] = useState<readonly OwnDeliveryOrder[]>([]);
  const [state, setState] = useState<ViewState>({ kind: "loading" });
  /*
   * Which sources did not answer, named.
   *
   * Reported beside the rows rather than instead of them. Three listings mean three ways to be
   * partly wrong, and this is the one screen in the application that makes three reads with no
   * component composing them — so the per-source reporting the experience tier does for its own
   * aggregates is done here, by hand, for the same reason.
   */
  const [missing, setMissing] = useState<readonly string[]>([]);

  const load = useCallback(() => {
    setState({ kind: "loading" });
    setMissing([]);
    const failures: string[] = [];
    void Promise.all([
      api
        .ownTrips(readWindow)
        .then(setTrips)
        .catch((error: unknown) => {
          failures.push(`rides (TRP-01): ${reasonFor(error)}`);
          setTrips([]);
        }),
      api
        .ownCommerceOrders(readWindow)
        .then(setOrders)
        .catch((error: unknown) => {
          failures.push(`shop orders (COM-05): ${reasonFor(error)}`);
          setOrders([]);
        }),
      api
        .ownDeliveryOrders(readWindow)
        .then(setParcels)
        .catch((error: unknown) => {
          failures.push(`parcels (DEL-03): ${reasonFor(error)}`);
          setParcels([]);
        }),
    ]).then(() => {
      setMissing(failures);
      /*
       * All three failing is an error screen; some failing is a history with a note on it.
       *
       * The distinction matters because the second is still useful and the first is not: a rider
       * whose rides loaded and whose parcels did not can still find the ride they came looking
       * for, and telling them the whole history is unavailable would be false.
       */
      if (failures.length === 3) {
        setState(
          stateForError(new Error("no source answered"), [
            SCOPES.ownTrips,
            SCOPES.ownCommerceOrders,
            SCOPES.ownDeliveryOrders,
          ]),
        );
        return;
      }
      setState({ kind: "ready" });
    });
  }, [api, readWindow]);

  useEffect(load, [load]);

  if (state.kind !== "ready") {
    return (
      <MarketState
        state={state}
        onRetry={load}
        onBack={props.onBack}
        purpose="Reading your rides, orders and parcels."
        barTitle="Activity"
        barSub="All"
      />
    );
  }

  const all = historyRows(trips, orders, parcels, now);
  const thisWeek = all.filter((entry) => isThisWeek(entry.at, now)).map((entry) => entry.row);
  const earlier = all.filter((entry) => !isThisWeek(entry.at, now)).map((entry) => entry.row);

  return (
    <MarketShell
      screen="order-history"
      left={{ glyph: "arrow_back", label: "Back", onPress: props.onBack }}
      barTitle="Activity"
      barSub="All"
      right={{
        glyph: "filter_list",
        label: "Filter",
        unavailable:
          "No filter: all three listings take only a window, and none takes a status or a kind",
      }}
      badge={{ kind: "idle", label: monthLabel(now) }}
      title="Rides, orders and parcels"
      sub="Tap any item for its receipt."
      flag="rows are not tappable: no receipt screen is designed in this deliverable and no operation returns one"
    >
      <Group heading="This week" rows={thisWeek} empty={<EmptyRow words="Nothing this week." />} />
      <Group heading="Earlier" rows={earlier} empty={<EmptyRow words="Nothing earlier." />} />
      {missing.length > 0 ? (
        <InfoNote
          text={`Some of your activity could not be loaded, so this list is not complete: ${missing.join("; ")}.`}
        />
      ) : null}
    </MarketShell>
  );
}
